/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { ReactElement, useState } from 'react'
import { useField } from 'remix-validated-form'
import { Button } from 'app/components/ui/button'
import FormInputWrapper, { BaseInputClasses } from './FormInputWrapper'

type Props = {
  name: string
  label: string
  addButtonLabel?: string
  placeholder?: string
  cols?: 1 | 2 | 3 | 4 | 5 | 6
  defaultValue?: string[]
  labelClassName?: string
  maxLength?: number
  inputMode?: `text` | `numeric` | `tel`
  /** Transform raw input into a display value. The raw value (before formatting) is submitted. */
  formatValue?: (value: string) => string
  /** Transform the input value before storing. Use for stripping characters, etc. */
  onChangeValue?: (value: string) => string
}

// Individual field component
function ArrayField({
  fieldName,
  fieldValue,
  placeholder,
  inputClasses,
  index,
  onRemove,
  onChange,
  maxLength,
  inputMode,
  formatValue,
  onChangeValue,
}: {
  fieldName: string
  fieldValue: string
  placeholder?: string
  inputClasses: string
  index: number
  inputMode?: `text` | `numeric` | `tel`
  maxLength?: number
  onRemove: (index: number) => void
  onChange: (index: number, value: string) => void
  formatValue?: (value: string) => string
  onChangeValue?: (value: string) => string
}) {
  const { getInputProps } = useField(fieldName)
  const displayValue = formatValue ? formatValue(fieldValue) : fieldValue

  return (
    <div className="flex items-center gap-x-3">
      <input
        value={displayValue}
        {...getInputProps({
          type: `text`,
          placeholder,
        })}
        {...(formatValue ? { name: undefined } : {})}
        maxLength={maxLength}
        inputMode={inputMode}
        onChange={(e) => {
          const val = onChangeValue ? onChangeValue(e.target.value) : e.target.value
          onChange(index, val)
        }}
        className={classNames(inputClasses, `flex-1`, `mb-0`)}
      />
      {formatValue && <input type="hidden" name={fieldName} value={fieldValue} />}
      <Button type="button" variant="subtle" size="icon" aria-label="Remove" onClick={() => onRemove(index)}>
        <TrashIcon className="h-5 w-5" />
      </Button>
    </div>
  )
}

export default function FormArrayInput({
  name,
  label,
  addButtonLabel = `Add Item`,
  placeholder = `Enter item`,
  cols = 6,
  defaultValue,
  labelClassName,
  maxLength,
  inputMode,
  formatValue,
  onChangeValue,
}: Props): ReactElement {
  const inputClasses = classNames(
    BaseInputClasses,
    `mb-2`,
    `read-only:cursor-not-allowed read-only:border-default read-only:bg-surface-subtle read-only:text-subtle`,
  )
  const { error } = useField(name)
  const [values, setValues] = useState<string[]>(defaultValue && defaultValue.length > 0 ? defaultValue : [``])

  const addInput = () => {
    setValues([...values, ``])
  }

  const removeInput = (index: number) => {
    if (values.length > 1) {
      // Remove the field when there are multiple items
      setValues((prev) => {
        const newValues = [...prev]
        newValues.splice(index, 1)
        return newValues
      })
    } else {
      // Clear the specific value when there's only one item
      setValues((prev) => {
        const newValues = [...prev]
        newValues[index] = ``
        return newValues
      })
    }
  }

  const handleChange = (index: number, value: string) => {
    setValues((prev) => {
      const newValues = [...prev]
      newValues[index] = value
      return newValues
    })
  }

  const addButton = (
    <Button type="button" variant="white" size="md" className="w-fit" onClick={addInput}>
      <PlusIcon className="h-4 w-4" />
      {addButtonLabel}
    </Button>
  )

  return (
    <>
      <FormInputWrapper
        name={name}
        label={label}
        error={error}
        cols={cols}
        wrapperClassName="text-heading-xs text-strong"
        className={labelClassName}
      >
        {values.map((value, index) => {
          return (
            <ArrayField
              key={`${name}-${index}`}
              fieldName={`${name}[${index}]`}
              fieldValue={value}
              placeholder={placeholder}
              inputClasses={inputClasses}
              index={index}
              onRemove={removeInput}
              onChange={handleChange}
              maxLength={maxLength}
              inputMode={inputMode}
              formatValue={formatValue}
              onChangeValue={onChangeValue}
            />
          )
        })}
        <div className="mt-2">{addButton}</div>
      </FormInputWrapper>
    </>
  )
}
